import { Col, Row } from 'antd';
import { useField } from 'formik';
import { useTranslation } from 'react-i18next';
import * as permissionKind from '../../../../../shared/permissions';
import { useAccount } from '../../../../components/contexts/AccountContextManager';
import { useCompany } from '../../../../components/contexts/CompanyContextManager';
import CollapsibleWrapper, { Panel } from '../../../../components/wrappers/CollapsibleWrapper';
import { useRuntimeConfig } from '../../../../runtimeConfig';
import FormFields from '../../../../themes/admin/Fields/FormFields';
import hasPermissions from '../../../../utilities/hasPermissions';
import usePasswordConfigurationOptions from '../../../../utilities/usePasswordConfigurationOptions';
import useSystemSwitchData from '../../../../utilities/useSystemSwitchData';
import useTooltip from '../../../../utilities/useTooltip';
import { useCompanyLanguageSelectedContext } from '../CompanyLanguageSelectedProvider';
import CompanyCustomerDataMaskFields from './CompanyCustomerDataMaskFields';
import CompanyEmailFields from './CompanyEmailFields';
import CompanySmsFields from './CompanySmsFields';

type CompanySetupFormProps = {
    updated?: string;
    offset?: string;
};
const CompanySetupForm = ({ updated, offset }: CompanySetupFormProps) => {
    const { t } = useTranslation(['companyDetails']);
    const { enableGenAI } = useRuntimeConfig();
    const { yesNoSwitch } = useSystemSwitchData();
    const passwordConfigurationOptions = usePasswordConfigurationOptions();
    const languages = useCompanyLanguageSelectedContext();
    const { permissions: accountPermissions } = useAccount();
    const company = useCompany(true);
    const hasPermission = company
        ? hasPermissions(company.permissions, [permissionKind.updateCompany]) &&
          hasPermissions(accountPermissions, [permissionKind.updateCompany])
        : hasPermissions(accountPermissions, [permissionKind.updateCompany]);

    const [{ value: isDataPurgedEnabled }] = useField('isDataPurgeEnabled');
    const dataPurgeAfterTooltip = useTooltip('dataPurgeAfter');

    return (
        <CollapsibleWrapper defaultActiveKey={t('companyDetails:cardTitles.companySetup')}>
            <Panel
                key={t('companyDetails:cardTitles.companySetup')}
                header={t('companyDetails:cardTitles.companySetup')}
            >
                <Row gutter={14}>
                    <Col lg={8} xs={24}>
                        <FormFields.InputField
                            {...t('companyDetails:fields.name', { returnObjects: true })}
                            disabled={!hasPermission}
                            name="displayName"
                            required
                        />
                    </Col>
                    <Col lg={8} xs={24}>
                        <FormFields.TranslatedInputField
                            {...t('companyDetails:fields.companyName', { returnObjects: true })}
                            disabled={!hasPermission}
                            languages={languages}
                            name="companyName"
                            required
                        />
                    </Col>
                    <Col lg={8} xs={24}>
                        <FormFields.TranslatedInputField
                            {...t('companyDetails:fields.legalName', { returnObjects: true })}
                            disabled={!hasPermission}
                            languages={languages}
                            name="legalName"
                            required
                        />
                    </Col>
                    <Col lg={8} xs={24}>
                        <FormFields.InputNumberField
                            {...t('companyDetails:fields.sessionTimeout', { returnObjects: true })}
                            addonAfter={t('companyDetails:suffixes.sessionTimeout')}
                            disabled={!hasPermission}
                            min={1}
                            name="sessionTimeout"
                            required
                        />
                    </Col>
                    <Col lg={8} xs={24}>
                        <FormFields.SelectField
                            {...t('companyDetails:fields.passwordConfiguration', { returnObjects: true })}
                            disabled={!hasPermission}
                            name="passwordConfiguration"
                            options={passwordConfigurationOptions}
                            required
                            showSearch
                        />
                    </Col>
                    <CompanyEmailFields />
                    <CompanySmsFields />
                    <CompanyCustomerDataMaskFields />
                    <Col lg={8} xs={24}>
                        <FormFields.SwitchField
                            {...t('companyDetails:fields.dataPurgeEnabled', { returnObjects: true })}
                            {...yesNoSwitch}
                            disabled={!hasPermission}
                            name="isDataPurgeEnabled"
                            required
                        />
                    </Col>
                    {isDataPurgedEnabled && (
                        <Col lg={8} xs={24}>
                            <FormFields.InputNumberField
                                {...t('companyDetails:fields.dataPurgeAfter', { returnObjects: true })}
                                addonAfter={t('companyDetails:suffixes.dataPurgeAfter')}
                                disabled={!hasPermission}
                                min={1}
                                name="dataPurgeAfter"
                                tooltip={dataPurgeAfterTooltip}
                                required
                            />
                        </Col>
                    )}
                    <Col lg={8} xs={24}>
                        <FormFields.SwitchField
                            {...t('companyDetails:fields.isInstantApprovalStatsEnabled', { returnObjects: true })}
                            {...yesNoSwitch}
                            disabled={!hasPermission}
                            name="isInstantApprovalStatsEnabled"
                            required
                        />
                    </Col>
                    {enableGenAI && (
                        <Col lg={8} xs={24}>
                            <FormFields.SwitchField
                                {...t('companyDetails:fields.enableContentRefinement', { returnObjects: true })}
                                {...yesNoSwitch}
                                disabled={!hasPermission}
                                name="enableContentRefinement"
                                required
                            />
                        </Col>
                    )}
                    <Col lg={8} xs={24}>
                        <FormFields.SwitchField
                            {...t('companyDetails:fields.active', { returnObjects: true })}
                            {...yesNoSwitch}
                            disabled={!hasPermission}
                            name="isActive"
                            required
                        />
                    </Col>
                    {updated && (
                        <Col lg={8} xs={24}>
                            <FormFields.DisplayFieldWithUTCOffset
                                {...t('common:fields.updated', { returnObjects: true, offset })}
                                value={updated}
                            />
                        </Col>
                    )}
                </Row>
            </Panel>
        </CollapsibleWrapper>
    );
};

export default CompanySetupForm;
